// Shapes from docs/onboarding-consignee/API-CONTRACT.md §2.

export type OnboardingStatus = 'pending' | 'completed' | 'skipped' | 'exempt';

export type StepResult = 'done' | 'skipped';

export type OnboardingStepKey = 'profil' | 'tampilan' | 'pernyataan' | 'consignee' | 'film';

export type OnboardingSteps = Partial<Record<OnboardingStepKey, StepResult>>;

export interface OnboardingState {
  /** No row on the server means `pending`; an impersonated request is `exempt`. */
  status: OnboardingStatus;
  steps: OnboardingSteps;
  /** The user has an active PPJK account. */
  hasPpjk: boolean;
  /** Any account of the PPJK predates the wizard, so the data steps never show. */
  ppjkIsOld: boolean;
  hasStatement: boolean;
  consigneeCount: number;
  updatedAt: string | null;
  /** A demo account: its wizard starts with a required profile step. */
  isDemo: boolean;
  /** The demo's prospect has submitted that profile. */
  demoProfileDone: boolean;
  /**
   * The server did not answer (network, 404 from an older backend). The gate
   * treats this as exempt; the wizard, once open, must NOT act on it — a
   * transient failure mid-run would otherwise read as "everything is set".
   */
  unavailable?: boolean;
}

export interface OnboardingUpdate {
  status?: OnboardingStatus;
  steps?: OnboardingSteps;
}

/** What the gate assumes when the server cannot answer: never block the app. */
export const EXEMPT_STATE: OnboardingState = {
  status: 'exempt',
  steps: {},
  hasPpjk: false,
  ppjkIsOld: true,
  hasStatement: true,
  consigneeCount: 0,
  updatedAt: null,
  isDemo: false,
  demoProfileDone: true,
};

/** Same shape, flagged so a refetch that failed is never mistaken for an answer. */
export const UNAVAILABLE_STATE: OnboardingState = { ...EXEMPT_STATE, unavailable: true };

const KNOWN_STATUSES: OnboardingStatus[] = ['pending', 'completed', 'skipped', 'exempt'];

/**
 * Shapes a server payload into a state. Anything that is not one of the four
 * statuses reads as `exempt`: a 200 with an unexpected body must not open
 * the wizard.
 */
export const normaliseOnboardingState = (raw: Partial<OnboardingState> | null | undefined): OnboardingState => ({
  status: KNOWN_STATUSES.includes(raw?.status as OnboardingStatus) ? (raw?.status as OnboardingStatus) : 'exempt',
  steps: raw?.steps && typeof raw.steps === 'object' ? raw.steps : {},
  hasPpjk: Boolean(raw?.hasPpjk),
  ppjkIsOld: Boolean(raw?.ppjkIsOld),
  hasStatement: Boolean(raw?.hasStatement),
  consigneeCount: typeof raw?.consigneeCount === 'number' ? raw.consigneeCount : 0,
  updatedAt: typeof raw?.updatedAt === 'string' ? raw.updatedAt : null,
  isDemo: raw?.isDemo === true,
  // Anything but an explicit false reads as done: never trap a user in a step
  // the server did not ask for.
  demoProfileDone: raw?.demoProfileDone !== false,
});

/** Order the wizard walks its steps in. */
export const STEP_ORDER: OnboardingStepKey[] = ['profil', 'tampilan', 'pernyataan', 'consignee', 'film'];

export interface StepContext {
  /**
   * The company's `master` module is on, the Master group is in this user's
   * menu, and Master › Pernyataan is one of its pages for this user. A step
   * whose page the user cannot open would save data they could never see or
   * change again (contract §2 plus module lock and role menus).
   */
  statementReachable: boolean;
  /** Same test for Master › Data Consignee. */
  consigneeReachable: boolean;
}

/** Both data pages equally reachable; convenient where they are not told apart. */
export const bothReachable = (reachable: boolean): StepContext => ({
  statementReachable: reachable,
  consigneeReachable: reachable,
});

/**
 * Which steps a state calls for. Contract §2: step 0 always; the data steps
 * only for a new PPJK that has not filled them. The film closes every run,
 * whatever came before it: even a user whose team already filled everything
 * is shown where it lives (see filmVariantFor / filmCopyFor).
 */
export const stepsFor = (
  state: OnboardingState,
  { statementReachable, consigneeReachable }: StepContext
): OnboardingStepKey[] => {
  if (state.isDemo) return demoStepsFor(state);
  const newPpjk = state.hasPpjk && !state.ppjkIsOld;
  const steps: OnboardingStepKey[] = ['tampilan'];
  if (newPpjk && statementReachable && !state.hasStatement) steps.push('pernyataan');
  if (newPpjk && consigneeReachable && state.consigneeCount === 0) steps.push('consignee');
  steps.push('film');
  return steps;
};

/**
 * A demo account's steps: who the prospect is (required, until submitted),
 * then appearance and the tour. The Pernyataan and consignee steps are left
 * out: a 24-hour trial on a made-up company has no use for them. A demo
 * whose first run already finished but never submitted a profile (an
 * account from before this step existed) is asked for the profile alone.
 */
export const demoStepsFor = (state: OnboardingState): OnboardingStepKey[] => {
  const steps: OnboardingStepKey[] = [];
  if (!state.demoProfileDone) steps.push('profil');
  if (state.status === 'pending' || state.demoProfileDone) steps.push('tampilan', 'film');
  return steps;
};

/** The gate must open: a demo has not told us who they are yet. */
export const demoProfileMissing = (state: OnboardingState): boolean => state.isDemo && !state.demoProfileDone;

/**
 * Which film to play, decided when the wizard opens. `full` walks Master,
 * Pernyataan, Data Consignee, then Pengaturan; `pengaturan` goes straight to
 * the footer, for users with no PPJK or no reachable Master menu.
 */
export type FilmVariant = 'full' | 'pengaturan';

export const filmVariantFor = (state: OnboardingState, masterReachable: boolean): FilmVariant =>
  state.hasPpjk && masterReachable ? 'full' : 'pengaturan';

/**
 * Which words go with the film. Each must be true for the state it is shown in.
 * - `filledNow`: data steps were on this run's rail.
 * - `teamFilled`: no data steps, and the PPJK really has both a Pernyataan and
 *   consignees (the team filled them).
 * - `masterInfo`: no data steps and Master is reachable, but the data is not
 *   (fully) there, e.g. an old PPJK that never set it up. Says where it lives,
 *   claims nothing about who filled what.
 * - `appearance`: no Master to show, only Pengaturan.
 */
export type FilmCopyVariant = 'filledNow' | 'teamFilled' | 'masterInfo' | 'appearance';

export const filmCopyFor = (
  variant: FilmVariant,
  dataStepsShown: boolean,
  state: Pick<OnboardingState, 'hasStatement' | 'consigneeCount'>
): FilmCopyVariant => {
  if (variant === 'pengaturan') return 'appearance';
  if (dataStepsShown) return 'filledNow';
  return state.hasStatement && state.consigneeCount > 0 ? 'teamFilled' : 'masterInfo';
};

/** The left rail's footer note for a film copy variant (copy key suffix). */
export const railNoteFor = (copy: FilmCopyVariant): 'default' | 'master' | 'appearance' => {
  if (copy === 'appearance') return 'appearance';
  return copy === 'filledNow' ? 'default' : 'master';
};

/**
 * Whether a wizard run may write onboarding progress (steps and status).
 *
 * A replay from the help menu by someone who is not `pending` writes nothing:
 * `ppjkIsOld` is derived from `exempt` rows, so a PPJK's only exempt account
 * replaying to `completed` would make the next colleague look like the first
 * user of a new PPJK. Data the user explicitly saves during a replay
 * (Pernyataan, consignee import) is not onboarding progress and still saves.
 */
export const shouldRecordOnboarding = ({
  openedManually,
  statusAtOpen,
}: {
  openedManually: boolean;
  statusAtOpen: OnboardingStatus;
}): boolean => !openedManually || statusAtOpen === 'pending';

/**
 * The Pernyataan step's "a colleague already filled this" panel: the PPJK now
 * has a Pernyataan, this user has not saved one in this run, has not chosen
 * to keep editing, and is not in the middle of their own save (their own
 * write also produces the realtime event).
 */
export const colleagueStatementPanelVisible = ({
  hasStatement,
  savedThisRun,
  dismissed,
  saving,
}: {
  hasStatement: boolean;
  savedThisRun: boolean;
  dismissed: boolean;
  saving: boolean;
}): boolean => hasStatement && !savedThisRun && !dismissed && !saving;

/** Same for the Data Consignee step, on the PPJK's consignee count. */
export const colleagueConsigneePanelVisible = ({
  consigneeCount,
  importedThisRun,
  dismissed,
  importing,
}: {
  consigneeCount: number;
  importedThisRun: boolean;
  dismissed: boolean;
  importing: boolean;
}): boolean => consigneeCount > 0 && !importedThisRun && !dismissed && !importing;

/** The Pernyataan a PPJK uses by default: the Utama row, else the first. */
export const pickUtamaStatement = <T extends { isDefault?: boolean }>(rows: T[] | null | undefined): T | null =>
  rows?.find((row) => row.isDefault) ?? rows?.[0] ?? null;

/** Whether the rail shows any data step this run. */
export const dataStepsShownIn = (keys: OnboardingStepKey[]): boolean =>
  keys.includes('pernyataan') || keys.includes('consignee');

/**
 * The state to plan from after a mid-wizard refetch: the fresh answer when
 * there is one, the current state when the server did not really answer.
 */
export const resolveAfterRefresh = (current: OnboardingState, fresh: OnboardingState | undefined): OnboardingState =>
  fresh && !fresh.unavailable ? fresh : current;

/** The step to show after `from`, or undefined when the wizard is done. */
export const nextStepAfter = (
  from: OnboardingStepKey,
  state: OnboardingState,
  context: StepContext
): OnboardingStepKey | undefined =>
  stepsFor(state, context).find((key) => STEP_ORDER.indexOf(key) > STEP_ORDER.indexOf(from));
